import * as THREE from 'three';

import type { PlaneFrame, PlaneVec2 } from '../../store/reducers/measurementPlanes.reducer.ts';
import type { SurfaceGeometry } from '../measurementCalculation/types.ts';
import { frameToWorld } from './frame.ts';

/**
 * Small visual lift (metres) so drawn outlines and measurement points sit just above
 * the model surface instead of z-fighting with it (which hid them until the layer was
 * raised). Purely visual — the computed point positions stay at the true height.
 */
export const RENDER_LIFT_M = 0.05;

/**
 * Triangulate a horizontal footprint polygon (world XY) into a flat BufferGeometry
 * at height `z`. Z-up world, so the surface normal is +Z. Step 1 (horizontal only);
 * oriented planes (slopes/façades) come in B1b.
 */
export function buildPlaneGeometry(
  polygon: PlaneVec2[],
  z: number,
  frame?: PlaneFrame,
  exclusions?: PlaneVec2[][],
): THREE.BufferGeometry {
  const shape = new THREE.Shape(polygon.map(([x, y]) => new THREE.Vector2(x, y)));
  // Subtract cut-out regions (rendered as holes in the filled footprint).
  for (const hole of exclusions ?? []) {
    if (hole.length < 3) continue;
    shape.holes.push(new THREE.Path(hole.map(([x, y]) => new THREE.Vector2(x, y))));
  }
  const geom = new THREE.ShapeGeometry(shape);
  if (frame) {
    // Map each triangulated (u, v) vertex into the oriented frame's world space.
    const pos = geom.attributes.position;
    for (let i = 0; i < pos.count; i++) {
      const w = frameToWorld([pos.getX(i), pos.getY(i)], frame);
      pos.setXYZ(i, w[0], w[1], w[2]);
    }
    pos.needsUpdate = true;
  } else {
    // ShapeGeometry lays the shape in the XY plane at z = 0 — lift it to the plane height.
    geom.translate(0, 0, z);
  }
  geom.computeVertexNormals();
  return geom;
}

/**
 * Triangulated plane as a portable SurfaceGeometry (positions + index). The engine
 * uses this only for road-direction (PCA) when a layer needs L/Ev/Esc — the actual
 * measurement points are supplied separately via `CalcLayer.points`.
 */
export function buildPlaneSurfaceGeometry(polygon: PlaneVec2[], z: number): SurfaceGeometry {
  const geom = buildPlaneGeometry(polygon, z);
  const positions = Array.from(geom.attributes.position.array as Float32Array);
  const index = geom.index ? Array.from(geom.index.array as ArrayLike<number>) : undefined;
  geom.dispose();
  return { positions, index };
}

/** Closed-loop line points for the polygon outline (world XY at `z`, or via `frame`). */
export function polygonOutline(polygon: PlaneVec2[], z: number, frame?: PlaneFrame): THREE.Vector3[] {
  const pts = polygon.map(([x, y]) => {
    if (frame) {
      const w = frameToWorld([x, y], frame);
      return new THREE.Vector3(w[0], w[1], w[2]);
    }
    return new THREE.Vector3(x, y, z);
  });
  if (pts.length > 1) pts.push(pts[0].clone());
  return pts;
}
